"use client";

import {
    Box,
    Heading,
    HStack,
    Icon,
    Skeleton,
    Text,
    VStack,
    SimpleGrid,
    Separator,
    Badge,
    Container,
} from "@chakra-ui/react";
import {
    FiCloudRain,
    FiThermometer,
    FiDroplet,
    FiSun,
    FiFileText,
    FiActivity,
} from "react-icons/fi";
import React, {useEffect, useState, useCallback} from "react";
import {LineChart} from "../Charts";
import {useParams} from "react-router-dom";
import {toaster} from "../ui/toaster";

const baseUrl = process.env.REACT_APP_API_URL;
const baseUrlWs = process.env.REACT_APP_API_URL_WS;

const DemoWeather = ({infoColor}) => {
    const token = localStorage.getItem("token");

    const [applicant, setApplicant] = useState(null);
    const [weather, setWeather] = useState(null);
    const [weatherAnalysis, setWeatherAnalysis] = useState("");
    const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

    const [isLoadingWeather, setIsLoadingWeather] = useState(true);
    // const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(true);
    // 1. Fetch Basic Data
    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingWeather(true);
            try {
                const [appRes, weatherRes] = await Promise.all([
                    fetch(`${baseUrl}/applicant/dashboard/demo/`, {
                        headers: {Authorization: `Bearer ${token}`, app_origin: "caz"},
                    }),
                    fetch(`${baseUrl}/applicant/dashboard/weather_e/demo/`, {
                        headers: {Authorization: `Bearer ${token}`, app_origin: "caz"},
                    }),
                ]);

                const appData = await appRes.json();
                const weatherData = await weatherRes.json();

                setApplicant(appData);
                setWeather(weatherData);
            } catch (error) {
                toaster.create({title: "Error", description: "Failed to load weather data", type: "error"});
            } finally {
                setIsLoadingWeather(false);
            }
        };
        fetchData();
    }, [token]);

    // 2. Automated Analysis Trigger
    // We use useCallback so the useEffect doesn't trigger unnecessarily
    // const runAnalysis = useCallback(async (currentWeather, currentApplicant) => {
    //     if (!currentWeather || weatherAnalysis ) return;
    //
    //     setIsLoadingAnalysis(true);
    //     try {
    //         const formData = new FormData();
    //         formData.append("weather", JSON.stringify(currentWeather));
    //         formData.append("location", currentApplicant?.meta?.location || "");
    //         formData.append("crop_type", currentApplicant?.meta?.crop_type || "");
    //
    //         const res = await fetch(`${baseUrl}/applicant/dashboard/weather_e/analysis/demo/`, {
    //             method: "POST",
    //             headers: {Authorization: `Bearer ${token}`, app_origin: "caz"},
    //             body: formData,
    //         });
    //         const data = await res.json();
    //         setWeatherAnalysis(data);
    //     } catch (error) {
    //         console.error("Analysis Error:", error);
    //     } finally {
    //         setIsLoadingAnalysis(false);
    //     }
    // }, [token, weatherAnalysis, isLoadingAnalysis]);

    // 1. Simplify the callback dependencies
    useEffect(() => {
        if (!weather || !applicant) return;

        let ws;
        let accumulatedText = "";
        let isCancelled = false; // prevents React strict mode double-run issues

        const connectWebSocket = () => {
            setIsLoadingAnalysis(true);
            setWeatherAnalysis("");

            const wsUrl = `${baseUrlWs}/applicant/dashboard/weather_e/ws/demo/`;
            console.log("Connecting to:", wsUrl);

            ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                console.log("WS OPENED");

                ws.send(JSON.stringify({
                    weather,
                    location: applicant?.meta?.location || "",
                    crop_type: applicant?.meta?.crop_type || "",
                }));
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);

                if (data.type === "chunk") {
                    accumulatedText += data.chunk;

                    // Avoid updating state if component unmounted
                    if (!isCancelled) {
                        setWeatherAnalysis(accumulatedText);
                    }
                }

                if (data.status === "complete") {
                    console.log("WS COMPLETE");
                    if (!isCancelled) {
                        setIsLoadingAnalysis(false);
                    }
                    ws.close();
                }

                if (data.status === "error") {
                    console.error("Server error:", data.detail);
                    if (!isCancelled) {
                        setIsLoadingAnalysis(false);
                    }
                    ws.close();
                }
            };

            ws.onerror = (err) => {
                console.error("WS ERROR:", err);
                if (!isCancelled) {
                    setIsLoadingAnalysis(false);
                }
                ws.close();
            };

            ws.onclose = (event) => {
                console.log("WS CLOSED:", event.code, event.reason);
            };
        };

        connectWebSocket();

        return () => {
            console.log("Cleanup triggered");
            isCancelled = true;

            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        };

    }, [weather, applicant]); // 🔥 ONLY depend on actual data

    return (
        <Container maxW="6xl" py={6}>
            <VStack align="stretch" gap={8}>
                {/* Header Section */}
                <HStack justify="space-between" align="flex-end">
                    <VStack align="start" gap={1}>
                        <HStack>
                            <Icon as={FiCloudRain} color="blue.500" boxSize={6}/>
                            <Heading size="xl" tracking="tight">Weather Assessment</Heading>
                        </HStack>
                        <Text color="fg.muted" fontSize="sm">
                            Automated climate analysis for {applicant?.meta?.location || "specified region"}
                        </Text>
                    </VStack>
                    {weather && (
                        <Badge variant="subtle" colorPalette={isLoadingAnalysis ? "blue" : "green"} size="lg" px={3}>
                        {isLoadingAnalysis ? "Generating Analysis..." : "Analysis Complete"}
                    </Badge>
                    )}
                    {/*<Badge variant="subtle" colorPalette={isLoadingAnalysis ? "blue" : "green"} size="lg" px={3}>*/}
                    {/*    {isLoadingAnalysis ? "Generating Analysis..." : "Analysis Complete"}*/}
                    {/*</Badge>*/}
                </HStack>

                <Separator/>

                {/* Data Grid: High Level Metrics */}
                <SimpleGrid columns={{base: 1, md: 3}} gap={6}>
                    <Skeleton loading={isLoadingWeather} borderRadius="xl">
                        <Box p={5} border="1px solid" borderColor="border.subtle" borderRadius="xl">
                            <HStack gap={4}>
                                <Box p={2} bg="orange.50" borderRadius="lg">
                                    <Icon as={FiThermometer} color="orange.600" boxSize={5}/>
                                </Box>
                                <VStack align="start" gap={0}>
                                    <Text fontSize="xs" color="fg.muted" fontWeight="bold">Current Temperature</Text>
                                    <Text fontSize="2xl"
                                          fontWeight="bold">{weather?.clima?.temperatura}ºF</Text>
                                </VStack>
                            </HStack>
                        </Box>
                    </Skeleton>

                    <Skeleton loading={isLoadingWeather} borderRadius="xl">
                        <Box p={5} border="1px solid" borderColor="border.subtle" borderRadius="xl">
                            <HStack gap={4}>
                                <Box p={2} bg="blue.50" borderRadius="lg">
                                    <Icon as={FiDroplet} color="blue.600" boxSize={5}/>
                                </Box>
                                <VStack align="start" gap={0}>
                                    <Text fontSize="xs" color="fg.muted" fontWeight="bold">Current Precipitation</Text>
                                    <Text fontSize="2xl"
                                          fontWeight="bold">{weather?.clima?.precipitacion} in</Text>
                                </VStack>
                            </HStack>
                        </Box>
                    </Skeleton>

                    <Skeleton loading={isLoadingWeather} borderRadius="xl">
                        <Box p={5} border="1px solid" borderColor="border.subtle" borderRadius="xl">
                            <HStack gap={4}>
                                <Box p={2} bg="yellow.50" borderRadius="lg">
                                    <Icon as={FiSun} color="yellow.600" boxSize={5}/>
                                </Box>
                                <VStack align="start" gap={0}>
                                    <Text fontSize="xs" color="fg.muted" fontWeight="bold">Forecast</Text>
                                    <Text fontSize="md" fontWeight="semibold" noOfLines={1}>
                                        {weather?.clima?.pronostico || "Stable Conditions"}
                                    </Text>
                                </VStack>
                            </HStack>
                        </Box>
                    </Skeleton>
                </SimpleGrid>

                {/* Chart Section */}
                <Box p={6} border="1px solid" borderColor="border.subtle" borderRadius="2xl" bg="gray.50/30">
                    <HStack mb={6} justify="space-between">
                        <VStack align="start" gap={0}>
                            <Text fontWeight="bold" fontSize="lg">Precipitation Trends</Text>
                            <Text fontSize="xs" color="fg.muted">Historical 12-month accumulation analysis</Text>
                        </VStack>
                        <Skeleton loading={isLoadingWeather} height="full">
                            <Badge size="md" variant="surface">
                                Accumulated Precipitation: {weather?.clima?.precipitacionAcumulada} in
                            </Badge>
                        </Skeleton>

                    </HStack>

                    <Box h="260px" w="full">
                        <Skeleton loading={isLoadingWeather} height="full">
                            {weather?.datosPrecipitacion && (
                                <LineChart
                                    data={weather.datosPrecipitacion}
                                    xKey="mes"
                                    yKey="precipitacion"
                                    color={infoColor || "#3182ce"}
                                    unit="in"
                                />
                            )}
                        </Skeleton>
                    </Box>
                </Box>

                {/* AI Narrative Section */}
                <VStack align="stretch" gap={4}>
                    <HStack gap={2}>
                        <Icon as={FiActivity} color="teal.500"/>
                        <Heading size="md">Weather Analysis</Heading>
                    </HStack>

                    <Box
                        p={6}
                        bg="white"
                        border="1px solid"
                        borderColor="border.subtle"
                        borderRadius="lg"
                        // boxShadow="sm"
                    >
                        {weatherAnalysis ? (
                            <Text
                                fontSize="md"
                                lineHeight="1.8"
                                color="gray.800"
                                whiteSpace="pre-wrap"
                            >
                                {weatherAnalysis}
                            </Text>
                        ) : isLoadingAnalysis ? (
                            <VStack align="start" gap={3}>
                                <Skeleton height="20px" width="100%"/>
                                <Skeleton height="20px" width="90%"/>
                                <Skeleton height="20px" width="95%"/>
                            </VStack>
                        ) : (
                            <Text color="fg.muted" fontSize="sm" fontStyle="italic">
                                Waiting for climate data...
                            </Text>
                        )}
                    </Box>
                </VStack>
            </VStack>
        </Container>
    );
};

export default DemoWeather;